import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, delay, filter, map, of, switchMap, tap } from 'rxjs';

import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { clearSession, saveSession } from '../../core/utils/session.util';
import { AuthActions } from './auth.actions';
import { selectAuthState } from './auth.selectors';

/** When the PIN reaches 4 digits, auto-submit for verification. */
export const autoVerifyPin$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(AuthActions.keyPress, AuthActions.setPin),
      concatLatestFrom(() => store.select(selectAuthState)),
      filter(([, auth]) => auth.pin.length === 4 && auth.status !== 'verifying'),
      delay(280),
      map(() => AuthActions.verifyPin()),
    ),
  { functional: true },
);

export const verifyPin$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), auth = inject(AuthService)) =>
    actions$.pipe(
      ofType(AuthActions.verifyPin),
      concatLatestFrom(() => store.select(selectAuthState)),
      switchMap(([, state]) =>
        auth.verifyPin(state.pin).pipe(
          map((res) =>
            AuthActions.verifyPinSuccess({
              token: res.token,
              accountId: res.accountId,
              storeId: res.storeId,
              terminalId: res.terminalId,
              storeInfo: res.storeInfo,
              assetName: res.assetName,
            }),
          ),
          catchError((err) =>
            of(
              AuthActions.verifyPinFailure({
                error: err?.error?.err ?? err?.error?.message ?? err?.message ?? 'Sign in failed',
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

/**
 * On success: persist the session and route into the console.
 * Data loading is left to ShellComponent's bootstrap so it fires exactly
 * once, whether the user just logged in or navigated straight to a route.
 */
export const onLoginSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), toast = inject(ToastService)) =>
    actions$.pipe(
      ofType(AuthActions.verifyPinSuccess),
      tap((res: ReturnType<typeof AuthActions.verifyPinSuccess>) => {
        // Persist the whole session so a browser refresh stays signed in.
        // Read back by the reducer (rehydrate) and the auth interceptor.
        saveSession({
          token: res.token,
          accountId: res.accountId,
          storeId: res.storeId,
          terminalId: res.terminalId,
          storeInfo: res.storeInfo,
          assetName: res.assetName,
        });
        toast.show('Signed in · ' + res.storeInfo.name);
        router.navigateByUrl('/catalog');
      }),
    ),
  { functional: true, dispatch: false },
);

export const onLogout$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        clearSession();
        router.navigateByUrl('/login');
      }),
    ),
  { functional: true, dispatch: false },
);
